import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Search, Plug, CreditCard, CheckCircle } from "lucide-react";
import { CHARGER_IMAGES } from "../constants/chargerImages";

const steps = [
  {
    number: "01",
    icon: Search,
    title: "DISCOVER",
    description:
      "Find the nearest MILE PLUS charging station using our app or website. Real-time availability updates help you plan your journey.",
    color: "primary",
  },
  {
    number: "02",
    icon: Plug,
    title: "CONNECT",
    description:
      "Simply plug in your EV. Our universal connectors support all major electric vehicle brands available in India.",
    color: "secondary",
  },
  {
    number: "03",
    icon: CreditCard,
    title: "CHARGE & PAY",
    description:
      "Monitor your charging progress in real-time. Pay securely through UPI, cards, or MILE PLUS wallet. Simple and transparent pricing.",
    color: "primary",
  },
  {
    number: "04",
    icon: CheckCircle,
    title: "GO GREEN",
    description:
      "Unplug and drive! Track your carbon savings and contribute to a cleaner Maharashtra with every charge.",
    color: "secondary",
  },
];

const HowItWorks = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });

  return (
    <section
      className="py-24 md:py-32 bg-background-paper relative overflow-hidden"
      data-testid="how-it-works-section"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(204,255,0,0.3) 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Simple Process
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            EV CHARGING IN
            <br />
            <span className="text-primary">4 SIMPLE STEPS</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            Getting your EV charged at MILE PLUS is quick, easy, and hassle-free.
            Here's how it works.
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-primary opacity-20 -translate-y-1/2" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative group"
                data-testid={`step-card-${index}`}
              >
                {/* Step Number Background */}
                <div className="absolute -top-4 -left-4 font-heading text-8xl text-white/5 select-none">
                  {step.number}
                </div>

                <div className="relative glass-card h-full">
                  {/* Step Number Badge */}
                  <div
                    className={`absolute -top-3 -right-3 w-10 h-10 rounded-full flex items-center justify-center font-heading text-sm ${
                      step.color === "primary"
                        ? "bg-primary text-black"
                        : "bg-secondary text-black"
                    }`}
                  >
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div
                    className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all group-hover:scale-110 ${
                      step.color === "primary"
                        ? "bg-primary/10 text-primary"
                        : "bg-secondary/10 text-secondary"
                    }`}
                  >
                    <step.icon className="w-8 h-8" />
                  </div>

                  {/* Content */}
                  <h3 className="font-heading text-2xl text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="text-foreground-muted leading-relaxed">
                    {step.description}
                  </p>

                  {/* Arrow for desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 translate-x-full -translate-y-1/2 text-primary/40">
                      <svg
                        width="32"
                        height="16"
                        viewBox="0 0 32 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M0 8H28M28 8L21 1M28 8L21 15"
                          stroke="currentColor"
                          strokeWidth="2"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-16"
        >
          {/* Charger Gallery */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {[
              { src: CHARGER_IMAGES.dc.zeus60kW, alt: "Zeus 60kW DC Charger" },
              { src: CHARGER_IMAGES.dc.zeus120kW, alt: "Zeus 120kW DC Charger" },
              { src: CHARGER_IMAGES.ac.zeus7kW, alt: "Zeus AC Charger" },
              { src: CHARGER_IMAGES.installations.highway, alt: "Highway Station" },
            ].map((img, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={isInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.7 + idx * 0.1 }}
                className="relative rounded-xl overflow-hidden group"
              >
                <img 
                  src={img.src} 
                  alt={img.alt}
                  className="w-full h-32 md:h-40 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <span className="text-white text-sm font-medium">{img.alt}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-foreground-muted mb-4">
            Ready to experience the future of mobility?
          </p>
          <a
            href="#stations"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            data-testid="how-it-works-cta"
          >
            Find a Station Near You
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.83331 14.1667L14.1666 5.83334M14.1666 5.83334H5.83331M14.1666 5.83334V14.1667"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;
